import store from 'store'

export const IP_GEO_CACHE_KEY = 'ipGeoSnapshotV1'

/** Sync read after {@link getIpGeoSnapshot} has populated the cache (e.g. after app hydration). */
export function getCachedIpGeoSnapshot() {
  return store.get(IP_GEO_CACHE_KEY) || null
}

/**
 * Ghana administrative regions (post-2019) → stable code for MTN / product use.
 * GeoIP databases may return names with or without "Region" and varying casing.
 */
const GHANA_REGION_CODES_ORDERED = [
  ['bono east', 'BONO_EAST'],
  ['north east', 'NORTH_EAST'],
  ['western north', 'WESTERN_NORTH'],
  ['greater accra', 'GREATER_ACCRA'],
  ['upper east', 'UPPER_EAST'],
  ['upper west', 'UPPER_WEST'],
  ['ahafo', 'AHAFO'],
  ['ashanti', 'ASHANTI'],
  ['bono', 'BONO'],
  ['central', 'CENTRAL'],
  ['eastern', 'EASTERN'],
  ['northern', 'NORTHERN'],
  ['oti', 'OTI'],
  ['savannah', 'SAVANNAH'],
  ['volta', 'VOLTA'],
  ['western', 'WESTERN']
]

function normalizeRegionLabel(raw) {
  if (!raw || typeof raw !== 'string') {
    return ''
  }
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+region$/i, '')
    .trim()
}

/**
 * Maps a GeoIP-style Ghana region name to a canonical code.
 * Returns null if the label is empty; UNKNOWN if Ghana but not matched.
 */
export function resolveMtnGhanaRegionCode(regionNameRaw) {
  const normalized = normalizeRegionLabel(regionNameRaw)
  if (!normalized) {
    return null
  }
  for (const [phrase, code] of GHANA_REGION_CODES_ORDERED) {
    if (normalized === phrase || normalized.startsWith(phrase + ' ')) {
      return code
    }
  }
  return 'UNKNOWN'
}

/**
 * Normalizes a GeoJS `asn` value (numeric or already `AS`-prefixed) to `AS<number>`.
 */
function normalizeAsn(asnRaw) {
  const trimmed = asnRaw != null ? String(asnRaw).trim() : ''
  if (!trimmed) {
    return null
  }
  return /^AS/i.test(trimmed) ? trimmed.toUpperCase() : `AS${trimmed}`
}

/**
 * Public HTTPS GeoJS endpoint (CORS). Resolves client IP on the server side.
 * Also returns ASN / carrier org name — used to disambiguate mobile operators that share a
 * country's IP geolocation (e.g. MTN Ghana vs Telecel Ghana, both `GH`). See
 * `viuHeaderEnrichment.js` / `VUE_APP_VIU_CARRIER_ASN_MAP_JSON` for how the ASN maps to a carrier.
 * @param {{ refresh?: boolean }} [options] - `refresh: true` skips local cache (e.g. re-check at phone login).
 */
export async function getIpGeoSnapshot(options = {}) {
  if (!options.refresh) {
    const cached = store.get(IP_GEO_CACHE_KEY)
    if (cached) {
      return cached
    }
  }

  try {
    const res = await fetch('https://get.geojs.io/v1/ip/geo.json')
    if (!res.ok) {
      throw new Error('geo http ' + res.status)
    }
    const data = await res.json()
    const snapshot = {
      countryCode: (data.country_code || '').toUpperCase(),
      regionName: data.region || null,
      city: data.city || null,
      asn: normalizeAsn(data.asn),
      organizationName: data.organization_name || data.organization || null
    }
    store.set(IP_GEO_CACHE_KEY, snapshot)
    return snapshot
  } catch {
    const fallback = {
      countryCode: '',
      regionName: null,
      city: null,
      asn: null,
      organizationName: null
    }
    return fallback
  }
}

/**
 * When the user's IP geolocates to Ghana, returns a canonical MTN-facing region code
 * derived from the subdivision (region) returned by the geo provider.
 */
export async function resolveMtnGhanaRegionFromUserIp() {
  const geo = await getIpGeoSnapshot()
  if (geo.countryCode !== 'GH') {
    return {
      countryCode: geo.countryCode || null,
      mtnGhanaRegionCode: null,
      regionName: geo.regionName
    }
  }

  return {
    countryCode: 'GH',
    mtnGhanaRegionCode: resolveMtnGhanaRegionCode(geo.regionName),
    regionName: geo.regionName
  }
}
